import {
    Component,
    effect,
    EventEmitter,
    Input,
    Output,
    Signal,
    ViewChild,
    WritableSignal,
} from '@angular/core';
import {
    FormControl,
    FormGroup,
    Validators,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
    MatSlideToggleChange,
    MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { Event } from '@angular/router';
import { Client } from 'src/app/models/client.model';
import { Project } from 'src/app/models/project-model';
import { ClientService } from 'src/app/services/client.service';
import { ProjectsService } from 'src/app/services/projects.service';
import { TaskService } from 'src/app/services/task-service.service';
import { Task } from 'src/app/models/task.model';
import { UpsertTaskComponent } from '../../upsert-task/upsert-task.component';
import { PastDateValidator } from 'src/app/validators/past-date.validator';
import { Page } from 'src/app/models/page.model';
import { ACTIVE, INACTIVE } from 'src/app/constants/statusConstant';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgFor, NgIf } from '@angular/common';
import { MatDivider } from '@angular/material/divider';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatCardModule } from '@angular/material/card';
import {
    COUNT,
    PROJECTID,
    STATUS,
} from 'src/app/constants/queryParamConstants';
import { UniversalSnackbarService } from 'src/app/services/universal-snackbar.service';
import { SnackbarMessages as MSG } from 'src/app/enums/snackBar.enum';
import { User } from 'src/app/models/User';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { COUNT_ALL } from 'src/app/constants/searchConstants';

interface IProjectFormControls {
    name: FormControl<string>;
    externalContact: FormControl<string>;
    internalContact: FormControl<string>;
    projectLead: FormControl<string>;
    startDate: FormControl<string>;
    endDate: FormControl<string>;
    notes: FormControl<string>;
    clientId: FormControl<string>;
    status: FormControl<boolean>;
    estimatedHours: FormControl<number | null>;
    nonBillable: FormControl<boolean>;
}

enum TaskAction {
    CREATE = 'CREATE',
    UPDATE = 'UPDATE',
    LOADING = 'LOADING',
    IDLE = 'IDLE',
}

@Component({
    selector: 'app-edit-project',
    templateUrl: './edit-project.component.html',
    styleUrls: ['./edit-project.component.scss'],
    imports: [
        NgIf,
        NgFor,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatCheckboxModule,
        MatSlideToggleModule,
        MatTableModule,
        MatIconModule,
        MatDivider,
        MatSort,
        MatSortModule,
        MatCardModule,
        MatOptionModule,
        MatSelectModule,
    ],
})
export class EditProjectComponent {
    @Input() project: Project = null;
    @Output() loadingProjectEdit = new EventEmitter<boolean>();
    @Output() projectChangeEvent = new EventEmitter<Project | null>();
    @Input() activeUsers: WritableSignal<User[]>;
    @Input() projectLeads: Signal<User[]>;
    viewingActiveTasks = true;
    failed = false;
    loading = false;
    editingTasks = false;
    tasks: Task[];
    client!: Client;
    taskAction: TaskAction = TaskAction.IDLE;
    displayedColumns: string[] = ['name', 'billType', 'action', 'edit'];
    dataSource: MatTableDataSource<Task>;
    @ViewChild(MatSort) sort: MatSort;

    projectForm: FormGroup<IProjectFormControls> = new FormGroup({
        name: new FormControl(),
        externalContact: new FormControl(),
        internalContact: new FormControl(),
        projectLead: new FormControl(),
        startDate: new FormControl(),
        endDate: new FormControl(),
        notes: new FormControl(),
        clientId: new FormControl(),
        status: new FormControl(),
        estimatedHours: new FormControl(),
        nonBillable: new FormControl(false),
    });

    get nonBillable(): boolean {
        return this.projectForm.get('nonBillable')?.value ?? false;
    }

    constructor(
        private clientService: ClientService,
        private projectService: ProjectsService,
        private taskService: TaskService,
        private dialog: MatDialog,
        private snackBar: UniversalSnackbarService,
    ) {
        this.watchLoadingState();
    }

    initializeForm(project?: Project): void {
        if (project) {
            this.taskAction = TaskAction.LOADING;
            this.updateTasks(project);
            this.projectForm = new FormGroup(
                {
                    name: new FormControl(project.name, [
                        Validators.required.bind(this),
                        Validators.maxLength(100),
                    ]),
                    externalContact: new FormControl(project.externalContact, [
                        Validators.required.bind(this),
                    ]),
                    internalContact: new FormControl(
                        {
                            value: project.internalContact ?? null,
                            disabled: this.activeUsers().length < 1,
                        },
                        [Validators.required.bind(this)],
                    ),
                    projectLead: new FormControl(
                        {
                            value: project.projectLead ?? null,
                            disabled: this.projectLeads().length < 1,
                        },
                        [Validators.required.bind(this)],
                    ),
                    startDate: new FormControl(project.startDate, [
                        Validators.required.bind(this),
                    ]),
                    endDate: new FormControl(project.endDate),
                    notes: new FormControl(project.notes),
                    clientId: new FormControl(project.clientId, [
                        Validators.required.bind(this),
                    ]),
                    status: new FormControl(
                        project.status.toLowerCase() === ACTIVE.toLowerCase()
                            ? true
                            : false,
                    ),
                    estimatedHours: new FormControl(
                        project.estimatedHours ?? null,
                    ),
                    nonBillable: new FormControl({
                        value: project.nonBillable,
                        disabled: true,
                    }),
                },
                [PastDateValidator],
            );
        } else if (this.project) {
            this.projectForm = new FormGroup(
                {
                    name: new FormControl(this.project.name, [
                        Validators.required.bind(this),
                        Validators.maxLength(100),
                    ]),
                    externalContact: new FormControl(
                        this.project.externalContact,
                        [Validators.required.bind(this)],
                    ),
                    internalContact: new FormControl(
                        {
                            value: this.project.internalContact ?? null,
                            disabled: this.activeUsers().length < 1,
                        },
                        [Validators.required.bind(this)],
                    ),
                    projectLead: new FormControl(
                        {
                            value: this.project.projectLead ?? null,
                            disabled: this.projectLeads().length < 1,
                        },
                        [Validators.required.bind(this)],
                    ),
                    startDate: new FormControl(this.project.startDate, [
                        Validators.required.bind(this),
                    ]),
                    endDate: new FormControl(this.project.endDate),
                    notes: new FormControl(this.project.notes),
                    clientId: new FormControl(this.project.clientId, [
                        Validators.required.bind(this),
                    ]),
                    status: new FormControl(
                        this.project.status.toLowerCase() ===
                        ACTIVE.toLowerCase()
                            ? true
                            : false,
                    ),
                    estimatedHours: new FormControl(
                        this.project.estimatedHours,
                    ),
                    nonBillable: new FormControl({
                        value: this.project.nonBillable,
                        disabled: true,
                    }),
                },
                [PastDateValidator],
            );
        } else {
            this.projectForm = new FormGroup({
                name: new FormControl(null, [
                    Validators.required.bind(this),
                    Validators.maxLength(100),
                ]),
                externalContact: new FormControl(null, [
                    Validators.required.bind(this),
                ]),
                internalContact: new FormControl(
                    {
                        value: null,
                        disabled: this.activeUsers().length < 1,
                    },
                    [Validators.required.bind(this)],
                ),
                projectLead: new FormControl(
                    {
                        value: null,
                        disabled: this.projectLeads().length < 1,
                    },
                    [Validators.required.bind(this)],
                ),
                startDate: new FormControl(null, [
                    Validators.required.bind(this),
                ]),
                endDate: new FormControl(null),
                notes: new FormControl(null),
                clientId: new FormControl(null, [
                    Validators.required.bind(this),
                ]),
                status: new FormControl(null),
                estimatedHours: new FormControl(null),
                nonBillable: new FormControl({ value: false, disabled: true }),
            });
        }
    }

    updateTasks(project: Project): void {
        this.loading = true;
        if (!this.client) {
            this.clientService
                .get([project.clientId])
                .subscribe((client: Client) => {
                    this.client = client;
                });
        }

        const params = new Map<string, number | string>([
            [STATUS, this.viewingActiveTasks ? ACTIVE : INACTIVE],
            [COUNT, COUNT_ALL],
            [PROJECTID, this.project.id],
        ]);

        this.taskService.get([], params).subscribe({
            next: (tasks: Page<Task>) => {
                this.loading = false;
                this.tasks = tasks.items;
                switch (this.taskAction) {
                    case TaskAction.CREATE:
                        this.snackBar.open(MSG.TASK_CREATED);
                        this.taskAction = TaskAction.IDLE;
                        break;
                    case TaskAction.UPDATE:
                        this.snackBar.open(MSG.TASK_UPDATED);
                        this.taskAction = TaskAction.IDLE;
                        break;
                }
            },
            error: () => {
                this.loading = false;
                switch (this.taskAction) {
                    case TaskAction.CREATE:
                        this.snackBar.open(MSG.TASK_CREATED);
                        this.taskAction = TaskAction.IDLE;
                        break;
                    case TaskAction.UPDATE:
                        this.snackBar.open(MSG.TASK_UPDATED);
                        this.taskAction = TaskAction.IDLE;
                        break;
                    case TaskAction.LOADING:
                        this.snackBar.open(MSG.TASK_LOADING_F);
                        this.taskAction = TaskAction.IDLE;
                        break;
                }
            },
        });
    }

    onActiveChange(event: MatSlideToggleChange): void {
        this.viewingActiveTasks = event.checked;
        this.updateTasks(this.project);
    }

    setEditProject(project: Project): void {
        this.project = project;
        this.initializeForm(project);
    }

    onFormSave(deselectUser?: boolean): void {
        this.projectForm.updateValueAndValidity();

        if (!this.projectForm.valid) {
            return;
        } else {
            this.loadingProjectEdit.emit(true);
            const project = new Project({
                name: this.projectForm.controls.name.value,
                externalContact:
                    this.projectForm.controls.externalContact.value,
                internalContact:
                    this.projectForm.controls.internalContact.value,
                projectLead: this.projectForm.controls.projectLead.value,
                startDate: this.projectForm.controls.startDate.value,
                endDate: this.projectForm.controls.endDate.value,
                clientId: this.projectForm.controls.clientId.value,
                nonBillable: this.projectForm.controls.nonBillable.value,
                estimatedHours: this.projectForm.controls.estimatedHours.value,
                notes: this.projectForm.controls.notes.value,
                status: '',
            });

            if (this.projectForm.controls.status.value === true) {
                project.status = ACTIVE;
            } else {
                project.status = INACTIVE;
            }

            project.nonBillable = this.nonBillable;

            this.projectService.put([this.project.id], project).subscribe({
                next: () => {
                    if (deselectUser) {
                        this.projectChangeEvent.emit(null);
                    }
                    this.loadingProjectEdit.emit(false);
                    this.project = null;
                    this.initializeForm();
                    this.snackBar.open(MSG.PROJECT_UPDATED);
                },
                error: () => {
                    this.failed = true;
                    this.snackBar.open(MSG.PROJECT_UPDATED_F);
                },
            });
        }
    }

    addTask(): void {
        const dialogRef = this.dialog.open(UpsertTaskComponent, {
            data: {
                title: 'New Task',
                task: null,
                clientName: this.client.name,
                projectName: this.project.name,
                projectId: this.project.id,
                approverRequired: false,
            },
        });

        dialogRef.afterClosed().subscribe((val) => {
            if (val) {
                this.taskAction = TaskAction.CREATE;
                this.updateTasks(this.project);
            }
        });
    }

    removeTask(task: Task): void {
        this.loading = true;
        task.status = INACTIVE;
        this.taskService.put([task.id], task).subscribe({
            next: () => {
                this.loading = false;
                this.snackBar.open(MSG.TASK_DEACTIVATED);
                this.updateTasks(this.project);
            },
            error: () => {
                this.loading = false;
                this.snackBar.open(MSG.TASK_DEACTIVATED_F);
            },
        });
    }

    makeTaskActive(task: Task): void {
        this.loading = true;
        task.status = ACTIVE;
        this.taskService.put([task.id], task).subscribe({
            next: () => {
                this.loading = false;
                this.snackBar.open(MSG.TASK_ACTIVATED);
                this.updateTasks(this.project);
            },
            error: () => {
                this.loading = false;
                this.snackBar.open(MSG.TASK_ACTIVATED_F);
            },
        });
    }

    editTask(task: Task): void {
        const dialogref = this.dialog.open(UpsertTaskComponent, {
            data: {
                title: 'Edit Task',
                task: task,
                clientName: this.client.name,
                projectName: this.project.name,
                projectId: this.project.id,
                approverRequired: task.approverRequired,
                billType: task.billType,
            },
        });

        dialogref.afterClosed().subscribe(() => {
            this.taskAction = TaskAction.UPDATE;
            this.updateTasks(this.project);
        });
    }

    editProjectTasks(event?: Event): void {
        if (!event) {
            this.editingTasks = true;
        } else {
            this.editingTasks = !this.editingTasks;
        }
    }

    getDataSource(): MatTableDataSource<Task> {
        this.dataSource = new MatTableDataSource(this.tasks);
        this.dataSource.sort = this.sort;
        return this.dataSource;
    }

    private watchLoadingState() {
        effect(() => {
            if (this.activeUsers().length < 1) {
                this.projectForm.get('internalContact')?.disable();
            } else {
                this.projectForm.get('internalContact')?.enable();
            }
            if (this.projectLeads().length < 1) {
                this.projectForm.get('projectLead')?.disable();
            } else {
                this.projectForm.get('projectLead')?.enable();
            }
        });
    }
}
